import { useState, useCallback } from "react";
import { Card, Descriptions, Table, Tag, Spin, Empty, Button, DatePicker, InputNumber, Slider, Space, Tooltip, message } from "antd";
import { EditOutlined, SaveOutlined, CloseOutlined, ReloadOutlined, LockOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  useGetJobQuery,
  useUpdateJobMutation,
  useRecalcJobMachineTimeMutation,
} from "../../../redux/api/scheduling";
import { toFacilityUTC, formatDateTime, formatShortDateTime } from "../_shared/utils/dateUtils";
import MachineOverrideSelect from "../_shared/components/MachineOverrideSelect";

const TIMELINESS_COLORS: Record<string, string> = { on_track: "green", at_risk: "orange", late: "red" };

interface Props {
  jobId: number;
}

export default function JobDetail({ jobId }: Props) {
  const { data: job, isLoading } = useGetJobQuery(jobId);
  const [updateJob] = useUpdateJobMutation();
  const [recalcMachineTime, { isLoading: isRecalcing }] = useRecalcJobMachineTimeMutation();
  const [editingDates, setEditingDates] = useState(false);
  const [materialReceived, setMaterialReceived] = useState<dayjs.Dayjs | null>(null);
  const [qcToolingReceived, setQcToolingReceived] = useState<dayjs.Dayjs | null>(null);
  const [toolingReceived, setToolingReceived] = useState<dayjs.Dayjs | null>(null);

  const patchField = useCallback(
    async (field: string, value: number | null) => {
      try {
        await updateJob({ id: jobId, [field]: value }).unwrap();
        message.success("Updated");
      } catch {
        message.error("Failed to update");
      }
    },
    [jobId, updateJob],
  );

  if (isLoading) return <Spin size="large" className="mt-10 mx-auto block" />;
  if (!job) return <Empty description="Job not found" />;

  const startEditing = () => {
    setMaterialReceived(job.material_received_date ? dayjs(job.material_received_date) : null);
    setQcToolingReceived(job.qc_tooling_received_date ? dayjs(job.qc_tooling_received_date) : null);
    setToolingReceived(job.tooling_received_date ? dayjs(job.tooling_received_date) : null);
    setEditingDates(true);
  };

  const saveDates = async () => {
    try {
      await updateJob({
        id: jobId,
        material_received_date: materialReceived ? toFacilityUTC(materialReceived) : null,
        qc_tooling_received_date: qcToolingReceived ? toFacilityUTC(qcToolingReceived) : null,
        tooling_received_date: toolingReceived ? toFacilityUTC(toolingReceived) : null,
      }).unwrap();
      message.success("Receiving dates updated");
      setEditingDates(false);
    } catch {
      message.error("Failed to update dates");
    }
  };

  const taskColumns = [
    { title: "Task ID", dataIndex: "task_task_id", key: "task_task_id", width: 80 },
    {
      title: "Task",
      key: "task_name",
      render: (_: any, r: any) =>
        r.batch_index > 0
          ? `${r.task_name} (Batch ${r.batch_index})`
          : r.task_name,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: string) => (
        <Tag color={v === "completed" ? "green" : v === "in_progress" ? "blue" : "default"}>{v}</Tag>
      ),
    },
    {
      title: "% Complete",
      dataIndex: "percent_complete",
      key: "percent_complete",
      render: (v: string) => `${v}%`,
    },
    {
      title: "Trigger %",
      dataIndex: "trigger_percent",
      key: "trigger_percent",
      render: (v: any) => v ? `${v}%` : "-",
    },
    {
      title: "Machine Time",
      dataIndex: "computed_machine_time_minutes",
      key: "computed_machine_time_minutes",
      render: (v: any) => v ? `${v} min` : "-",
      width: 100,
    },
    {
      title: "Staff Time",
      dataIndex: "computed_staff_time_minutes",
      key: "computed_staff_time_minutes",
      render: (v: any) => v ? `${v} min` : "-",
      width: 100,
    },
    {
      title: "Latest Start",
      dataIndex: "latest_start_date",
      key: "latest_start_date",
      render: (v: string) => v ? formatShortDateTime(v) : "-",
    },
    {
      title: "Scheduled Start",
      dataIndex: "start_date",
      key: "start_date",
      render: (v: string) => v ? formatShortDateTime(v) : "-",
    },
    {
      title: "Scheduled End",
      dataIndex: "end_date",
      key: "end_date",
      render: (v: string) => v ? formatShortDateTime(v) : "-",
    },
    { title: "Machine", dataIndex: "assigned_machine_name", key: "assigned_machine_name", render: (v: any) => v || "-" },
    { title: "Machine Group", dataIndex: "assigned_machine_group_name", key: "assigned_machine_group_name", render: (v: any) => v || "-" },
    { title: "Staff", dataIndex: "assigned_staff_name", key: "assigned_staff_name", render: (v: any) => v || "-" },
  ];

  const exceptionColumns = [
    {
      title: "Type",
      dataIndex: "exception_type",
      key: "exception_type",
      render: (v: string) => <Tag color="red">{v.replace(/_/g, " ").toUpperCase()}</Tag>,
    },
    { title: "Message", dataIndex: "message", key: "message" },
  ];

  const fmt = (v: string | null) => (v ? formatDateTime(v, "MM/DD/YYYY HH:mm") : "-");

  const EditableMinutes = ({ field, value }: { field: string; value: number }) => (
    <InputNumber
      size="small"
      min={0}
      defaultValue={value || 0}
      addonAfter="min"
      style={{ width: 140 }}
      onBlur={(e) => {
        const v = parseInt(e.target.value, 10);
        if (!isNaN(v) && v !== value) patchField(field, v);
      }}
    />
  );

  const EditableInterval = ({ field, value }: { field: string; value: number | null }) => (
    <InputNumber
      size="small"
      min={1}
      defaultValue={value ?? undefined}
      style={{ width: 100 }}
      onBlur={(e) => {
        const v = parseInt(e.target.value, 10);
        if (!isNaN(v) && v !== value) patchField(field, v);
      }}
    />
  );

  return (
    <div>
      <Card className="mb-4">
        <Descriptions
          title={
            <div>
              {`${job.order_name} – ${job.part_name}`}
              {job.is_internal && (
                <div style={{ marginTop: 4 }}>
                  <Tag color="purple">Internal</Tag>
                </div>
              )}
            </div>
          }
          bordered
          size="small"
          column={2}
        >
          <Descriptions.Item label="Quantity">{job.quantity}</Descriptions.Item>
          <Descriptions.Item label="Status"><Tag>{job.status}</Tag></Descriptions.Item>
          <Descriptions.Item label="Lead Time"><Tag>{job.lead_time}</Tag></Descriptions.Item>
          <Descriptions.Item label="Timeliness">
            <Tag color={TIMELINESS_COLORS[job.timeliness]}>{job.timeliness?.replace(/_/g, " ").toUpperCase()}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Job Mix"><Tag>{job.job_mix || "—"}</Tag></Descriptions.Item>
          <Descriptions.Item label="Received">{fmt(job.received_date)}</Descriptions.Item>
          <Descriptions.Item label="Required Completion">{fmt(job.required_completion_date)}</Descriptions.Item>
          <Descriptions.Item label="Adjusted Completion">{fmt(job.adjusted_required_completion_date)}</Descriptions.Item>
          <Descriptions.Item label="Machine Time">
            <Space size="small">
              <EditableMinutes field="machine_time_minutes" value={job.machine_time_minutes} />
              {job.machine_time_manual_override && (
                <Tooltip title="Manually set — CAM updates won't auto-overwrite. Click 'Recalc from CAM' to revert to the CAM-derived value.">
                  <Tag color="gold" icon={<LockOutlined />} style={{ marginLeft: 4 }}>
                    Manual
                  </Tag>
                </Tooltip>
              )}
              <Tooltip title="Recompute Machine Time from the OLI's CAM Program Data (clears the Manual lock).">
                <Button
                  size="small"
                  type="text"
                  icon={<ReloadOutlined />}
                  loading={isRecalcing}
                  onClick={async () => {
                    try {
                      const res: any = await recalcMachineTime(jobId).unwrap();
                      if (res?.changed) {
                        message.success(`Recalculated from CAM: ${res.machine_time_minutes} min`);
                      } else if (res?.detail) {
                        message.info(res.detail);
                      } else {
                        message.success("Already up to date with CAM");
                      }
                    } catch {
                      message.error("Failed to recalculate from CAM");
                    }
                  }}
                >
                  CAM
                </Button>
              </Tooltip>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Delivery Time">
            <EditableMinutes field="delivery_time_minutes" value={job.delivery_time_minutes} />
          </Descriptions.Item>
          <Descriptions.Item label="Cutting Time">
            <EditableMinutes field="material_cutting_time_minutes" value={job.material_cutting_time_minutes} />
          </Descriptions.Item>
          <Descriptions.Item label="Machine Setup Time">
            <EditableMinutes field="machine_setup_time_minutes" value={job.machine_setup_time_minutes} />
          </Descriptions.Item>
          <Descriptions.Item label="Slack">
            <EditableMinutes field="slack_minutes" value={job.slack_minutes} />
          </Descriptions.Item>
          <Descriptions.Item label="Quality Interval">
            <EditableInterval field="quality_interval" value={job.quality_interval} />
          </Descriptions.Item>
          <Descriptions.Item label="Finishing Machine Time">
            <EditableMinutes field="finishing_machine_time_minutes" value={job.finishing_machine_time_minutes} />
          </Descriptions.Item>
          <Descriptions.Item label="Finishing Staff Time">
            <EditableMinutes field="finishing_staff_time_minutes" value={job.finishing_staff_time_minutes} />
          </Descriptions.Item>
          <Descriptions.Item label="Run Difficulty">
            <Space>
              <Slider
                min={1}
                max={10}
                value={job.run_difficulty ?? 5}
                style={{ width: 150 }}
                onChange={async (v) => {
                  try {
                    await updateJob({ id: jobId, run_difficulty: v }).unwrap();
                  } catch { /* silent */ }
                }}
              />
              <Tag>{job.run_difficulty ?? 5} / 10</Tag>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Machine Group">
            {job.machine_group_assignments?.length > 0
              ? job.machine_group_assignments.map((a: any) => (
                  <Tag key={a.task_name}>{a.task_name}: {a.group_name}</Tag>
                ))
              : "-"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Machine Override" className="mb-4">
        <MachineOverrideSelect jobId={jobId} />
      </Card>

      <Card
        title="Receiving Dates"
        className="mb-4"
        extra={
          editingDates ? (
            <Space>
              <Button icon={<SaveOutlined />} type="primary" size="small" onClick={saveDates}>Save</Button>
              <Button icon={<CloseOutlined />} size="small" onClick={() => setEditingDates(false)}>Cancel</Button>
            </Space>
          ) : (
            <Button icon={<EditOutlined />} size="small" onClick={startEditing}>Edit</Button>
          )
        }
      >
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Material Received Date">
            {editingDates ? (
              <DatePicker
                showTime
                value={materialReceived}
                onChange={setMaterialReceived}
                format="MM/DD/YYYY HH:mm"
                allowClear
              />
            ) : (
              fmt(job.material_received_date)
            )}
          </Descriptions.Item>
          <Descriptions.Item label="QC Tooling Received Date">
            {editingDates ? (
              <DatePicker
                showTime
                value={qcToolingReceived}
                onChange={setQcToolingReceived}
                format="MM/DD/YYYY HH:mm"
                allowClear
              />
            ) : (
              fmt(job.qc_tooling_received_date)
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Tooling Received Date">
            {editingDates ? (
              <DatePicker
                showTime
                value={toolingReceived}
                onChange={setToolingReceived}
                format="MM/DD/YYYY HH:mm"
                allowClear
              />
            ) : (
              fmt(job.tooling_received_date)
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Job Tasks" className="mb-4">
        <Table dataSource={job.job_tasks || []} columns={taskColumns} rowKey="id" size="small" pagination={false} />
      </Card>

      {(job.exceptions || []).length > 0 && (
        <Card title="Exceptions">
          <Table dataSource={job.exceptions} columns={exceptionColumns} rowKey="id" size="small" pagination={false} />
        </Card>
      )}
    </div>
  );
}
